export type VeeraContextualThreadRole = "leader" | "coach";

export type VeeraContextualThreadEntrySurface =
  | "today"
  | "insights"
  | "reflection"
  | "leader_card"
  | "post_meeting_review"
  | "portfolio";

export type VeeraThreadOpenerQuestionMode =
  | "action"
  | "understanding"
  | "processing"
  | "leader_coaching"
  | "coach_reflection"
  | "portfolio";

export interface VeeraContextualThreadInputTransport {
  role: VeeraContextualThreadRole;
  entrySurface: VeeraContextualThreadEntrySurface;
  primaryEntityId: string;
  topicFingerprint: string;
  contextualTopicId?: string | null;
  sourceId?: string | null;
}

export interface VeeraContextualAssistantOpenerTransport {
  sourceLabel: string;
  structure: ["carry-over summary", "why it matters now", "question"];
  carryOverSummary: string;
  whyItMattersNow: string;
  questionMode: VeeraThreadOpenerQuestionMode;
  shouldAskQuestion: boolean;
  question: string | null;
}

interface BaseVeeraContextualEntryTransport {
  version?: 1;
  role: VeeraContextualThreadRole;
  surface: VeeraContextualThreadEntrySurface;
  threadTitle: string;
  sourceLabel: string;
  topicFingerprint: string;
  contextualThreadInput: VeeraContextualThreadInputTransport;
  assistantOpener: VeeraContextualAssistantOpenerTransport;
}

export interface LeaderVeeraContextualEntryTransport extends BaseVeeraContextualEntryTransport {
  role: "leader";
  surface: "today" | "insights" | "reflection";
}

export interface CoachVeeraContextualEntryTransport extends BaseVeeraContextualEntryTransport {
  role: "coach";
  surface: "leader_card" | "post_meeting_review" | "portfolio";
  contextType: "global" | "leader" | "coach";
  leaderId: string | null;
  leaderName: string | null;
}

export type VeeraContextualEntryTransport =
  | LeaderVeeraContextualEntryTransport
  | CoachVeeraContextualEntryTransport;

const ENCODED_PREFIX = "ctx1.";
const DEFAULT_STRUCTURE: VeeraContextualAssistantOpenerTransport["structure"] = [
  "carry-over summary",
  "why it matters now",
  "question",
];

function normalizeText(value: unknown) {
  if (typeof value !== "string") return null;
  const normalized = value.replace(/\s+/g, " ").trim();
  return normalized || null;
}

function normalizeNullableText(value: unknown) {
  return normalizeText(value) ?? null;
}

function normalizeTopicFingerprint(value: unknown) {
  const normalized = normalizeText(value)?.toLowerCase();
  if (!normalized) return "unknown";
  return normalized
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .replace(/-{2,}/g, "-") || "unknown";
}

function normalizeContextualTopicId(value: unknown) {
  const normalized = normalizeText(value)?.toLowerCase();
  if (!normalized) return null;
  return normalized
    .replace(/[^a-z0-9|]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .replace(/-{2,}/g, "-") || null;
}

function buildContextualTopicId(params: {
  role: VeeraContextualThreadRole;
  entrySurface: VeeraContextualThreadEntrySurface;
  primaryEntityId: string;
  topicFingerprint: string;
  contextualTopicId?: unknown;
}) {
  const explicitTopicId = normalizeContextualTopicId(params.contextualTopicId);
  if (explicitTopicId) return explicitTopicId;

  return [
    params.role,
    params.entrySurface,
    params.primaryEntityId,
    params.topicFingerprint,
  ]
    .map((value) => normalizeTopicFingerprint(value))
    .join("|");
}

function normalizeRole(value: unknown): VeeraContextualThreadRole | null {
  return value === "leader" || value === "coach" ? value : null;
}

function normalizeSurface(value: unknown): VeeraContextualThreadEntrySurface | null {
  switch (value) {
    case "today":
    case "insights":
    case "reflection":
    case "leader_card":
    case "post_meeting_review":
    case "portfolio":
      return value;
    default:
      return null;
  }
}

function inferRoleFromSurface(surface: VeeraContextualThreadEntrySurface | null) {
  if (!surface) return null;
  return surface === "today" || surface === "insights" || surface === "reflection"
    ? "leader"
    : "coach";
}

function normalizeQuestionMode(value: unknown): VeeraThreadOpenerQuestionMode | null {
  switch (value) {
    case "action":
    case "understanding":
    case "processing":
    case "leader_coaching":
    case "coach_reflection":
    case "portfolio":
      return value;
    default:
      return null;
  }
}

function normalizeContextType(value: unknown): CoachVeeraContextualEntryTransport["contextType"] | null {
  switch (value) {
    case "global":
    case "leader":
    case "coach":
      return value;
    default:
      return null;
  }
}

function normalizeThreadInput(
  value: unknown,
  fallbackRole: VeeraContextualThreadRole,
  fallbackSurface: VeeraContextualThreadEntrySurface,
  fallbackTopicFingerprint: string,
) {
  if (!value || typeof value !== "object") return null;
  const threadInput = value as Partial<VeeraContextualThreadInputTransport>;
  const role = normalizeRole(threadInput.role) ?? fallbackRole;
  const entrySurface = normalizeSurface(threadInput.entrySurface) ?? fallbackSurface;
  const primaryEntityId = normalizeText(threadInput.primaryEntityId);
  if (!primaryEntityId) return null;
  const topicFingerprint = normalizeTopicFingerprint(threadInput.topicFingerprint ?? fallbackTopicFingerprint);

  return {
    role,
    entrySurface,
    primaryEntityId,
    topicFingerprint,
    contextualTopicId: buildContextualTopicId({
      role,
      entrySurface,
      primaryEntityId,
      topicFingerprint,
      contextualTopicId: threadInput.contextualTopicId,
    }),
    sourceId: normalizeNullableText(threadInput.sourceId) ?? undefined,
  } satisfies VeeraContextualThreadInputTransport;
}

function normalizeAssistantOpener(
  value: unknown,
  fallbackSourceLabel: string,
) {
  if (!value || typeof value !== "object") return null;
  const opener = value as Partial<VeeraContextualAssistantOpenerTransport>;
  const carryOverSummary = normalizeText(opener.carryOverSummary);
  const whyItMattersNow = normalizeText(opener.whyItMattersNow);
  const questionMode = normalizeQuestionMode(opener.questionMode);
  if (!carryOverSummary || !whyItMattersNow || !questionMode) return null;

  return {
    sourceLabel: normalizeText(opener.sourceLabel) ?? fallbackSourceLabel,
    structure: DEFAULT_STRUCTURE,
    carryOverSummary,
    whyItMattersNow,
    questionMode,
    shouldAskQuestion: opener.shouldAskQuestion === true,
    question: normalizeNullableText(opener.question),
  } satisfies VeeraContextualAssistantOpenerTransport;
}

function encodeBase64Url(value: string) {
  const bytes = new TextEncoder().encode(value);
  let binary = "";
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }
  return btoa(binary)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/g, "");
}

function decodeBase64Url(value: string) {
  const normalized = value
    .replace(/-/g, "+")
    .replace(/_/g, "/");
  const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, "=");
  const binary = atob(padded);
  const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}

function parseUnknownTransport(raw: string) {
  if (!raw) return null;

  try {
    if (raw.startsWith(ENCODED_PREFIX)) {
      return JSON.parse(decodeBase64Url(raw.slice(ENCODED_PREFIX.length)));
    }
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function coerceVeeraContextualEntryTransport(
  value: unknown,
): VeeraContextualEntryTransport | null {
  if (!value || typeof value !== "object") return null;

  const entry = value as Partial<VeeraContextualEntryTransport>;
  const surface = normalizeSurface(entry.surface);
  const role = normalizeRole(entry.role) ?? inferRoleFromSurface(surface);
  const threadTitle = normalizeText(entry.threadTitle);
  const sourceLabel = normalizeText(entry.sourceLabel);
  const topicFingerprint = normalizeTopicFingerprint(
    entry.topicFingerprint ?? entry.contextualThreadInput?.topicFingerprint,
  );

  if (!surface || !role || !threadTitle || !sourceLabel) {
    return null;
  }

  const contextualThreadInput = normalizeThreadInput(
    entry.contextualThreadInput,
    role,
    surface,
    topicFingerprint,
  );
  const assistantOpener = normalizeAssistantOpener(entry.assistantOpener, sourceLabel);

  if (!contextualThreadInput || !assistantOpener) {
    return null;
  }

  if (role === "leader") {
    if (surface !== "today" && surface !== "insights" && surface !== "reflection") {
      return null;
    }
    return {
      version: 1,
      role,
      surface,
      threadTitle,
      sourceLabel,
      topicFingerprint,
      contextualThreadInput,
      assistantOpener,
    };
  }

  if (surface !== "leader_card" && surface !== "post_meeting_review" && surface !== "portfolio") {
    return null;
  }

  return {
    version: 1,
    role,
    surface,
    contextType: normalizeContextType(entry.contextType) ?? (surface === "portfolio" ? "global" : "leader"),
    leaderId: normalizeNullableText(entry.leaderId),
    leaderName: normalizeNullableText(entry.leaderName),
    threadTitle,
    sourceLabel,
    topicFingerprint,
    contextualThreadInput,
    assistantOpener,
  };
}

export function parseVeeraContextualEntryTransport(
  raw: string | null | undefined,
): VeeraContextualEntryTransport | null {
  if (!raw) return null;
  return coerceVeeraContextualEntryTransport(parseUnknownTransport(raw));
}

export function serializeVeeraContextualEntryTransport(
  entry: VeeraContextualEntryTransport,
) {
  return `${ENCODED_PREFIX}${encodeBase64Url(JSON.stringify({
    version: 1,
    ...entry,
  }))}`;
}
